import React, { useState, useEffect } from 'react';
import DataCollection from '../../shared/DataCollection';
import ProtocolCardList from './ProtocolCardList.js';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';

const SYSTEM_ACCOUNT = "000000000000000000000000";

export const ListProtocols = ({ onEdit, planType }) => {
  // Reproduction plans are "protocols" in the UI; herd/weight ones are "plans".
  const isIatf = !planType || planType === 'iatf';
  const { t } = useTranslation();
  const collection = 'lots.plans';
  const searchFields = planType ? { type: planType } : undefined;
  const [actions, setActions] = useState([]);

  useEffect(() => {
    Fetch('api/corral/actions', 'get', null,
      (data) => setActions(data || []),
      () => {}
    );
  }, []);

  const getProtocolDuration = (timeline_days) => {
    if (!timeline_days || timeline_days.length === 0) return '-';
    const maxDay = Math.max(...timeline_days.map(d => d.end_day || d.start_day || 0));
    return `${maxDay} ${t("days")}`;
  };

  const getTargetAnimals = (targets) => {
    if (!targets || targets.length === 0 || !Array.isArray(targets)) return '-';
    return targets.join(', ');
  };

  const ProtocolExpandedComponent = ({ data }) => {
    const isSystemProtocol = data.account === SYSTEM_ACCOUNT;
    const actionLabel = (id) => actions.find(a => a._id === id)?.label || id;
    return (
      <div className={`protocol-expanded ${isSystemProtocol ? 'readonly' : ''}`}>
        {isSystemProtocol && (
          <div className="system-protocol-notice">
            {t(isIatf ? 'system_protocol_readonly' : 'system_plan_readonly')}
          </div>
        )}
        <div className="protocol-details">
          {data.description && (
            <div className="detail-section">
              <strong>{t('description')}:</strong> {data.description}
            </div>
          )}
          {data.notes && (
            <div className="detail-section">
              <strong>{t('notes')}:</strong> {data.notes}
            </div>
          )}
          <div className="detail-section">
            <strong>{t('timeline')}:</strong>
            <div className="timeline-readonly">
              {[...(data.timeline_days || [])].sort((a, b) => a.start_day - b.start_day).map((day, index) => (
                <div key={index} className="timeline-day-readonly">
                  <div className="day-label">
                    {t('day')} {day.start_day}{day.end_day !== day.start_day ? ` - ${day.end_day}` : ''}:
                  </div>
                  {day.actions && day.actions.length > 0 && (
                    <ul className="treatments-list">
                      {day.actions.map((action, aIndex) => (
                        <li key={aIndex}>{action}</li>
                      ))}
                    </ul>
                  )}
                  {day.action_ids && day.action_ids.length > 0 && (
                    <ul className="treatments-list">
                      {day.action_ids.map((id, aIndex) => (
                        <li key={aIndex}>{actionLabel(id)}</li>
                      ))}
                    </ul>
                  )}
                  {day.note && (
                    <div className="treatment-note">{day.note}</div>
                  )}
                  {day.system_action && (
                    <div className="sa-tooltip-wrapper">
                      <div className="system-action-badge">
                        <span>⚙️</span>
                        <span>{t(`system_action_${day.system_action}`)}</span>
                      </div>
                      <div className="sa-tooltip">
                        <span className="sa-tooltip-title">{t(`system_action_${day.system_action}_tooltip_title`)}</span>
                        <span className="sa-tooltip-body">{t(`system_action_${day.system_action}_tooltip`)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="protocol-actions">
          {!isSystemProtocol && (
            <button className="edit-protocol-btn" onClick={() => onEdit(data)}>
              {t(isIatf ? 'edit_protocol' : 'edit_plan')}
            </button>
          )}
        </div>
      </div>
    );
  };

  const columns = [
    { name: t("name"),           selector: row => row.name,                               sortable: true },
    { name: t("duration"),       selector: row => getProtocolDuration(row.timeline_days), sortable: true },
    { name: t("target_animals"), selector: row => getTargetAnimals(row.target_animals) },
    {
      name: t("protocol_type"),
      selector: row => row.account === SYSTEM_ACCOUNT ? t("protocol_type_system") : t("protocol_type_custom"),
      sortable: true,
    },
  ];

  return (
    <DataCollection
      collection={collection}
      searchFields={searchFields}
      columns={columns}
      sortField={4}
      expandableRows={true}
      expandableRowsComponent={({ data }) => (
        <ProtocolExpandedComponent data={data} />
      )}
      mobileComponent={(data, onRefresh) => (
        <ProtocolCardList data={data} onRefresh={onRefresh} onEdit={onEdit} actions={actions} isIatf={isIatf} />
      )}
    />
  );
}

export default ListProtocols;
